// Project-activity accordion data — fetches the chronological activity feed
// lazily on first expand. Server caches 60 s so a quick collapse/expand
// cycle doesn't re-fan-out to GitLab.

import { useCallback, useEffect, useRef, useState } from "react";

import {
  ProjectActivityResponseSchema,
  type ActivityEvent,
} from "@loom/shared/dashboard";

import { httpGet, UnauthorizedError } from "../lib/http";

export interface ProjectActivityState {
  readonly events: ReadonlyArray<ActivityEvent>;
  readonly loading: boolean;
  readonly error: string | null;
  readonly refetch: () => void;
}

export const useProjectActivity = (
  projectId: number,
  enabled: boolean,
): ProjectActivityState => {
  const [events, setEvents] = useState<ReadonlyArray<ActivityEvent>>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [tick, setTick] = useState<number>(0);
  const aborted = useRef(false);

  useEffect(() => {
    if (!enabled) return;
    aborted.current = false;
    setLoading(true);
    setError(null);
    httpGet(
      `/api/projects/${projectId}/activity`,
      ProjectActivityResponseSchema,
    )
      .then((value) => {
        if (aborted.current) return;
        setEvents(value.events);
        setLoading(false);
      })
      .catch((err: unknown) => {
        if (aborted.current) return;
        if (err instanceof UnauthorizedError) return;
        setError(err instanceof Error ? err.message : String(err));
        setLoading(false);
      });
    return () => {
      aborted.current = true;
    };
  }, [projectId, enabled, tick]);

  const refetch = useCallback(() => setTick((n) => n + 1), []);
  return { events, loading, error, refetch };
};
